/** 快捷变更单草稿：沿用正式变更单的销售及管理角色，不增加线上项目经理权限。
 * 复用现有 POST/PUT/submit 接口，保存草稿和提交审核分开；正式文件继续由 CoDocDialog 管理。
 */
import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { errTextOf } from "../lib/detailText";

// 与服务端 CO_CREATE_ROLES 保持一致，避免旧稿遗漏销售经理或意外放开 cdc。
export const canAuthorCO = (role) => ["sales", "sales_manager", "sales_vp", "branch_sales_manager", "admin", "ceo"].includes(role);
// 正式文件包含结构化明细，必须从原文件编辑器修改，避免只改摘要导致预览内容不同步。
export const canEditCO = (co) => co?.canEdit !== false && ["Draft", "Revision Requested"].includes(co?.status)
  && !co?.doc && !(co?.payments || []).length && !co?.invoice?.invoiceNumber;
const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400";

/** projectId 固定项目，co 指定已有草稿；成功保存后 reload 刷新列表，onClose 返回原入口。 */
export default function ChangeOrderEditor({ projectId = "", co = null, onClose, reload }) {
  const { lang, api } = useApp();
  const zh = lang === "zh";
  const [projects, setProjects] = useState([]);
  const [draftId, setDraftId] = useState(co?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const form = useRef(null);
  const saving = useRef(false);
  const [f, setF] = useState({
    projectId: co?.projectId || projectId, changeReason: co?.changeReason || "",
    customerRequest: co?.customerRequest || "", newScope: co?.newScope || "",
    addedWork: co?.addedWork || "", removedWork: co?.removedWork || "",
    estimatedPrice: co?.estimatedPrice ?? "", estimatedScheduleDays: co?.estimatedScheduleDays ?? "",
  });
  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || (zh ? "项目加载失败" : "Unable to load projects")));
  }, [api, zh]);
  /** 先保存再提交；审核请求失败时保留草稿 ID，重试只更新该草稿，防止重复创建。 */
  const save = async (submit) => {
    if (saving.current) return;
    const invalid = (text, selector) => {
      setError(text); form.current?.querySelector(selector)?.focus();
    };
    if (!f.projectId || !f.newScope.trim()) {
      invalid(zh ? "请选择项目并填写变更范围" : "Select a project and enter the change scope", !f.projectId ? "select" : '[data-testid="co-scope-input"]'); return;
    }
    if (!Number.isFinite(Number(f.estimatedPrice || 0)) || Number(f.estimatedPrice || 0) < 0) {
      invalid(zh ? "预估价格须为不小于零的有效数字" : "Enter a valid non-negative price", '[data-testid="co-price-input"]'); return;
    }
    if (!Number.isInteger(Number(f.estimatedScheduleDays || 0)) || Number(f.estimatedScheduleDays || 0) < 0) {
      invalid(zh ? "工期须为不小于零的整数" : "Enter non-negative whole schedule days", '[data-testid="co-days-input"]'); return;
    }
    setError("");
    saving.current = true; setBusy(true);
    let saved = false;
    try {
      // 当前后端编辑契约是 PUT；旧稿的 PATCH 会返回 405，导致已有草稿始终无法保存。
      const { data } = draftId ? await api.put(`/change-orders/${draftId}`, f) : await api.post("/change-orders", f);
      setDraftId(data.id); saved = true;
      if (submit) await api.post(`/change-orders/${data.id}/submit`);
      toast.success(submit ? (zh ? "已提交工程审核" : "Submitted for engineering review") : (zh ? "草稿已保存" : "Draft saved"));
      reload(); onClose();
    } catch (e) {
      const detail = errTextOf(e?.response?.data?.detail) || e.message;
      const text = saved ? (zh ? `草稿已保存，提交失败：${detail}` : `Draft saved; submission failed: ${detail}`) : detail;
      setError(text); toast.error(text);
      if (saved) reload();
    } finally { saving.current = false; setBusy(false); }
  };
  const fields = [
    ["changeReason", "变更原因", "Change reason"], ["customerRequest", "客户要求", "Customer request"],
    ["newScope", "变更范围 / 工作", "Change scope / work", true],
    ["addedWork", "增加的工作", "Added work", true], ["removedWork", "减少的工作", "Removed work", true],
  ];
  return <Dialog open onOpenChange={(open) => { if (!open && !saving.current) onClose(); }}>
    <DialogContent className="max-w-lg z-[100]" overlayClassName="z-[99]" closeDisabled={busy} closeLabel={zh ? "关闭" : "Close"} data-testid="co-create-dialog">
      <DialogHeader><DialogTitle>{co ? (zh ? "编辑 Change Order" : "Edit Change Order") : (zh ? "新建变更订单" : "New Change Order")}</DialogTitle>
        <DialogDescription>{zh ? "填写变更范围、预估金额与工期，可保存草稿或提交工程审核。" : "Enter the scope, estimated price and schedule. Save a draft or submit for engineering review."}</DialogDescription></DialogHeader>
      {error && <p role="alert" className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">{errTextOf(error)}</p>}
      <fieldset ref={form} disabled={busy} className="space-y-3">
        <label className="block"><span className="text-xs text-slate-500">{zh ? "项目" : "Project"}</span>
          <select data-testid="co-project-select" className={inp} value={f.projectId} disabled={!!projectId || !!draftId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id} label={p.clientName ? `${p.address} · ${p.clientName}` : p.address} />)}
          </select>
        </label>
        {fields.map(([key, cn, en, multiline]) => <label key={key} className="block"><span className="text-xs text-slate-500">{zh ? cn : en}</span>
          {multiline ? <textarea data-testid={key === "newScope" ? "co-scope-input" : `co-${key}-input`} rows={2} className={inp} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} />
            : <input data-testid={`co-${key}-input`} className={inp} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} />}
        </label>)}
        <div className="grid grid-cols-2 gap-3">
          <label><span className="text-xs text-slate-500">{zh ? "预估价格 $" : "Estimated price $"}</span><input data-testid="co-price-input" type="number" min="0" step="0.01" className={inp} value={f.estimatedPrice} onChange={(e) => setF({ ...f, estimatedPrice: e.target.value })} /></label>
          <label><span className="text-xs text-slate-500">{zh ? "工期影响（天）" : "Schedule impact (days)"}</span><input data-testid="co-days-input" type="number" min="0" step="1" className={inp} value={f.estimatedScheduleDays} onChange={(e) => setF({ ...f, estimatedScheduleDays: e.target.value })} /></label>
        </div>
      </fieldset>
      <div className="flex gap-2">
        <Button data-testid="co-draft-save-btn" disabled={busy} variant="outline" onClick={() => save(false)} className="flex-1">{zh ? "保存草稿" : "Save draft"}</Button>
        <Button data-testid="co-save-btn" disabled={busy} onClick={() => save(true)} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存并提交审核" : "Save & submit")}</Button>
      </div>
    </DialogContent>
  </Dialog>;
}
